import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Circle } from "lucide-react";

import { PIPELINE_API } from "./project-tabs/constants";

const API = PIPELINE_API;

const PRESENCE_COLORS = ["#014d7d", "#178a55", "#6941c6", "#c8841a", "#b84040", "#0e7490", "#e55d25", "#0f172a"];

export const CollaborationPresence = ({ projectId, activeTab }) => {
  const [activeUsers, setActiveUsers] = useState([]);
  const heartbeatRef = useRef(null);
  const prevTab = useRef(activeTab);

  useEffect(() => {
    // Initial fetch
    axios.get(`${API}/projects/${projectId}/presence`, { withCredentials: true })
      .then(res => setActiveUsers(res.data.active_users || []))
      .catch(() => {});

    // Heartbeat every 20s
    heartbeatRef.current = setInterval(() => {
      axios.post(`${API}/projects/${projectId}/presence/heartbeat`, { cursor: prevTab.current || "overview" }, { withCredentials: true })
        .then(res => setActiveUsers(res.data.active_users || []))
        .catch(() => {});
    }, 20000);

    return () => clearInterval(heartbeatRef.current);
  }, [projectId]);

  // Broadcast tab change immediately
  useEffect(() => {
    prevTab.current = activeTab;
    axios.post(`${API}/projects/${projectId}/presence/heartbeat`, { cursor: activeTab || "overview" }, { withCredentials: true })
      .then(res => setActiveUsers(res.data.active_users || []))
      .catch(() => {});
  }, [activeTab, projectId]);

  if (activeUsers.length === 0) return null;

  const TAB_LABELS = { overview: "Overview", board: "Board", tasks: "Tasks", assets: "Assets", analytics: "Analytics", conversations: "Chat", pipelines: "Pipelines", dependencies: "Deps", export: "Export", team: "Team", comments: "Comments" };

  return (
    <div className="flex items-center gap-2" data-testid="collab-presence">
      <div className="flex -space-x-1.5">
        {activeUsers.slice(0, 5)?.map((u, i) => (
          <div
            key={u.user_id || i}
            className="relative group/pres"
          >
            <div
              className="w-7 h-7 rounded-full border-2 border-white flex items-center justify-center text-white text-[8px] font-bold relative cursor-default"
              style={{ background: PRESENCE_COLORS[i % PRESENCE_COLORS.length] }}
              data-testid={`presence-user-${u.user_id || i}`}
            >
              {(u.name || "?").slice(0, 2).toUpperCase()}
              <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-white" />
            </div>
            {/* Hover tooltip showing what tab the user is on */}
            <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 bg-foreground text-white text-[10px] font-semibold px-2.5 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover/pres:opacity-100 pointer-events-none transition-opacity z-50 shadow-lg">
              <span>{u.name}</span>
              {u.cursor && (
                <span className="ml-1 opacity-70">
                  on {TAB_LABELS[u.cursor] || u.cursor}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      {activeUsers.length > 5 && (
        <span className="text-[10px] text-foreground/50 font-medium">+{activeUsers.length - 5}</span>
      )}
      <span className="text-[10px] text-emerald-600 font-semibold ml-0.5 flex items-center gap-0.5">
        <Circle className="w-1.5 h-1.5 fill-emerald-400 text-emerald-400" />
        {activeUsers.length} online
      </span>
    </div>
  );
};
